import { ActionIcon, Badge, Button, Group, Menu, Paper, Select, Switch, Table, Text, TextInput } from '@mantine/core';
import { modals } from '@mantine/modals';
import { IconArchive, IconArchiveOff, IconCopy, IconDots, IconEdit, IconPlus, IconSearch, IconStar, IconStarFilled, IconTemplate, IconTrash } from '@tabler/icons-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import {
  useClients,
  useDeleteProject,
  useDuplicateProject,
  useProjects,
  useSaveProject,
  useToggleFavorite,
  type Project,
} from '@/api/catalog';
import { useCurrentWorkspace } from '@/app/WorkspaceContext';
import { PageHeader } from '@/components/PageHeader';
import { ProjectDot } from '@/components/ProjectDot';
import { useApiError } from '@/hooks/useApiError';
import { ProjectModal } from './ProjectModal';

/** Projektliste mit Filtern und Aktionen (PRJ-12). */
export function ProjectsPage() {
  const { t } = useTranslation();
  const ctx = useCurrentWorkspace();
  const { workspaceId } = ctx;
  const [search, setSearch] = useState('');
  const [clientId, setClientId] = useState<string | null>(null);
  const [archived, setArchived] = useState(false);
  const [templates, setTemplates] = useState(false);
  const { data: projects = [], isPending } = useProjects(workspaceId, {
    search: search || undefined,
    clientId: clientId ?? undefined,
    archived,
    template: templates,
  });
  const { data: clients = [] } = useClients(workspaceId);
  const save = useSaveProject(workspaceId);
  const remove = useDeleteProject(workspaceId);
  const favorite = useToggleFavorite(workspaceId);
  const duplicate = useDuplicateProject(workspaceId);
  const onError = useApiError();
  const [modal, setModal] = useState<{ open: boolean; project?: Project }>({ open: false });
  const canCreate =
    ctx.isAdmin ||
    ctx.workspace.settings.permissions.createProjects === 'EVERYONE' ||
    (ctx.workspace.settings.permissions.createProjects === 'MANAGERS' && ctx.isManager);

  const confirmDelete = (p: Project) =>
    modals.openConfirmModal({
      title: t('projects.deleteTitle'),
      children: <Text size="sm">{t('projects.deleteText', { name: p.name })}</Text>,
      labels: { confirm: t('common.delete'), cancel: t('common.cancel') },
      confirmProps: { color: 'red' },
      onConfirm: () => remove.mutate(p.id, { onError }),
    });

  return (
    <>
      <PageHeader title={t('nav.items.projects')}>
        {canCreate && (
          <Button leftSection={<IconPlus size={16} />} onClick={() => setModal({ open: true })}>
            {t('projects.create')}
          </Button>
        )}
      </PageHeader>
      <Paper withBorder p="sm" mb="md">
        <Group>
          <TextInput
            leftSection={<IconSearch size={14} />}
            placeholder={t('common.search')}
            value={search}
            onChange={(e) => setSearch(e.currentTarget.value)}
            w={220}
          />
          <Select
            placeholder={t('projects.allClients')}
            data={clients.map((c) => ({ value: c.id, label: c.name }))}
            value={clientId}
            onChange={setClientId}
            clearable
            searchable
            w={200}
          />
          <Switch label={t('projects.showArchived')} checked={archived} onChange={(e) => setArchived(e.currentTarget.checked)} />
          {ctx.hasFeature('PROJECT_TEMPLATES') && (
            <Switch label={t('projects.showTemplates')} checked={templates} onChange={(e) => setTemplates(e.currentTarget.checked)} />
          )}
        </Group>
      </Paper>
      <Paper withBorder>
        <Table.ScrollContainer minWidth={640}>
          <Table highlightOnHover verticalSpacing="sm">
            <Table.Thead>
              <Table.Tr>
                <Table.Th w={40} />
                <Table.Th>{t('projects.name')}</Table.Th>
                <Table.Th>{t('projects.client')}</Table.Th>
                <Table.Th>{t('projects.access')}</Table.Th>
                <Table.Th>{t('projects.billable')}</Table.Th>
                <Table.Th w={50} />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {projects.map((p) => (
                <Table.Tr key={p.id}>
                  <Table.Td>
                    <ActionIcon
                      variant="subtle"
                      color={p.favorite ? 'yellow' : 'gray'}
                      onClick={() => favorite.mutate({ id: p.id, favorite: !p.favorite }, { onError })}
                      aria-label={t('projects.favorite')}
                    >
                      {p.favorite ? <IconStarFilled size={16} /> : <IconStar size={16} />}
                    </ActionIcon>
                  </Table.Td>
                  <Table.Td>
                    <Group gap="xs">
                      <Link to={`/projects/${p.id}`} style={{ textDecoration: 'none' }}>
                        <ProjectDot name={p.name} color={p.color} />
                      </Link>
                      {p.template && <Badge size="xs" variant="light">{t('projects.template')}</Badge>}
                      {p.archived && <Badge size="xs" color="gray">{t('common.archived')}</Badge>}
                    </Group>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm" c={p.clientName ? undefined : 'dimmed'}>
                      {p.clientName ?? '—'}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Badge variant="outline" color={p.isPublic ? 'gray' : 'orange'} size="sm">
                      {p.isPublic ? t('projects.public') : t('projects.private')}
                    </Badge>
                  </Table.Td>
                  <Table.Td>{p.billable ? t('common.yes') : t('common.no')}</Table.Td>
                  <Table.Td>
                    {p.canManage && (
                      <Menu position="bottom-end" withinPortal>
                        <Menu.Target>
                          <ActionIcon variant="subtle" color="gray" aria-label={t('common.more')}>
                            <IconDots size={16} />
                          </ActionIcon>
                        </Menu.Target>
                        <Menu.Dropdown>
                          <Menu.Item leftSection={<IconEdit size={14} />} onClick={() => setModal({ open: true, project: p })}>
                            {t('common.edit')}
                          </Menu.Item>
                          {canCreate && (
                            <Menu.Item
                              leftSection={<IconCopy size={14} />}
                              onClick={() => duplicate.mutate({ id: p.id, name: `${p.name} (${t('projects.copy')})`, template: false }, { onError })}
                            >
                              {t('projects.duplicate')}
                            </Menu.Item>
                          )}
                          {canCreate && ctx.hasFeature('PROJECT_TEMPLATES') && !p.template && (
                            <Menu.Item
                              leftSection={<IconTemplate size={14} />}
                              onClick={() => duplicate.mutate({ id: p.id, name: `${p.name} (${t('projects.template')})`, template: true }, { onError })}
                            >
                              {t('projects.saveAsTemplate')}
                            </Menu.Item>
                          )}
                          <Menu.Item
                            leftSection={p.archived ? <IconArchiveOff size={14} /> : <IconArchive size={14} />}
                            onClick={() => save.mutate({ id: p.id, archived: !p.archived }, { onError })}
                          >
                            {p.archived ? t('common.restore') : t('common.archive')}
                          </Menu.Item>
                          {ctx.isAdmin && (
                            <Menu.Item color="red" leftSection={<IconTrash size={14} />} onClick={() => confirmDelete(p)}>
                              {t('common.delete')}
                            </Menu.Item>
                          )}
                        </Menu.Dropdown>
                      </Menu>
                    )}
                  </Table.Td>
                </Table.Tr>
              ))}
              {!isPending && projects.length === 0 && (
                <Table.Tr>
                  <Table.Td colSpan={6}>
                    <Text c="dimmed" ta="center" py="lg">
                      {t('projects.empty')}
                    </Text>
                  </Table.Td>
                </Table.Tr>
              )}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      </Paper>
      <ProjectModal opened={modal.open} project={modal.project} onClose={() => setModal({ open: false })} />
    </>
  );
}
